import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  SectionHeading, FilterSearch, FilterToggle, StatusBadge, DomainCard,
  Button, EmptyState, ICON_PX, useToast,
} from '../ui';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useWorkflowChat } from '../../hooks/useWorkflowChat';
import { getWorkflowAIService } from '../../services/workflowAIService';
import { machineToNodesEdges } from '../../schemas/workflowSchema';
import { ensureV2 } from '../../utils/workflowMigration';
import WorkflowCanvas from '../shared/WorkflowCanvas';
import WorkflowSidebar from '../shared/WorkflowSidebar';
import WorkflowExecutionList from '../shared/WorkflowExecutionList';
import CreateWorkflowDialog from '../shared/CreateWorkflowDialog';
import DeleteWorkflowDialog from '../shared/DeleteWorkflowDialog';

const REGULATORY_BADGE = {
  joint_commission: { label: 'Joint Commission', variant: 'info' },
  osha: { label: 'OSHA', variant: 'watch' },
  cms: { label: 'CMS', variant: 'info' },
};

const WF_DOMAIN_MAP = {
  quality: 'quality', staffing: 'staffing', access: 'access',
  finance: 'finance', compliance: 'practice', safety: 'practice',
  operations: 'ops', communications: 'practice',
};

function PMChecklistsTab({ readOnly = false, initialWorkflowId }) {
  const {
    checklists, taskLibrary, workflows,
    createWorkflow, updateWorkflow, deleteWorkflow,
  } = usePracticeManagement();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [canvasMode, setCanvasMode] = useState('execute');
  const [viewMode, setViewMode] = useState('list');
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(null);

  // Auto-select workflow when navigating from Priority Queue
  useEffect(() => {
    if (initialWorkflowId) setActiveWorkflowId(initialWorkflowId);
  }, [initialWorkflowId]);

  // ── Unified workflow list (real templates + legacy checklists) ──
  const allWorkflows = useMemo(() => {
    const items = [];
    workflows.forEach((wf) => {
      // Support both v1 (flat nodes) and v2 (machine) formats
      let taskCount = 0;
      if (wf.machine?.states) {
        taskCount = Object.values(wf.machine.states).filter((s) => s.meta?.nodeType === 'task').length;
      } else {
        taskCount = (wf.nodes || []).filter((n) => n.type === 'task').length;
      }
      items.push({
        source: 'template',
        id: wf.id,
        name: wf.name,
        domain: wf.domain,
        cadence: wf.cadence,
        regulatorySource: wf.regulatorySource,
        stepsTotal: taskCount,
        stepsCompleted: 0,
        raw: wf,
      });
    });
    checklists.forEach((cl) => {
      items.push({
        source: 'checklist',
        id: cl.id,
        name: cl.name,
        domain: cl.domain,
        cadence: cl.cadence,
        regulatorySource: cl.regulatorySource,
        stepsTotal: cl.stepsTotal,
        stepsCompleted: cl.stepsCompleted,
        raw: cl,
      });
    });
    if (search) {
      const q = search.toLowerCase();
      return items.filter((i) => i.name.toLowerCase().includes(q) || (i.domain || '').toLowerCase().includes(q));
    }
    return items;
  }, [workflows, checklists, search]);

  const activeItem = useMemo(
    () => allWorkflows.find((w) => w.id === activeWorkflowId),
    [allWorkflows, activeWorkflowId]
  );

  const activeTemplate = activeItem?.source === 'template' ? activeItem.raw : null;

  // ── Convert active workflow to v2 and extract flat nodes/edges ──
  const activeV2 = useMemo(() => {
    if (!activeTemplate) return null;
    return ensureV2(activeTemplate);
  }, [activeTemplate]);

  // ── Build mode editing state (undo/redo) ──
  const editorInitialState = useMemo(() => {
    if (activeV2?.machine) {
      const { nodes, edges } = machineToNodesEdges(activeV2.machine, activeV2.swimlanes);
      return { nodes, edges };
    }
    if (activeTemplate) {
      return { nodes: activeTemplate.nodes || [], edges: activeTemplate.edges || [] };
    }
    if (activeItem?.source === 'checklist') {
      const cl = activeItem.raw;
      const nodes = cl.steps.map((step) => ({
        id: step.id,
        type: 'task',
        position: { x: 0, y: 0 },
        label: step.description,
        taskId: null,
        domain: cl.domain,
        completionType: step.requiresNote ? 'checkbox_note' : 'checkbox',
        estimatedMinutes: null,
      }));
      nodes.push({
        id: `${cl.id}-end`,
        type: 'end',
        position: { x: 0, y: 0 },
        label: 'Complete',
      });
      const edges = [];
      for (let i = 0; i < cl.steps.length; i++) {
        const target = i < cl.steps.length - 1 ? cl.steps[i + 1].id : `${cl.id}-end`;
        edges.push({ id: `edge-${cl.steps[i].id}-${target}`, from: cl.steps[i].id, to: target });
      }
      return { nodes, edges };
    }
    return { nodes: [], edges: [] };
  }, [activeV2, activeTemplate, activeItem]);

  const { state: editorState, set: setEditorState, undo, redo, canUndo, canRedo, reset: resetEditor } = useUndoRedo(editorInitialState);
  const [editorDirty, setEditorDirty] = useState(false);

  useEffect(() => {
    resetEditor(editorInitialState);
    setEditorDirty(false);
  }, [activeWorkflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI Chat ──
  const handleAIApply = useCallback((schema) => {
    // Convert v2 schema to flat nodes/edges and update editor
    const { nodes, edges } = machineToNodesEdges(schema.machine, schema.swimlanes);
    setEditorState({ nodes, edges });
    setEditorDirty(true);
  }, [setEditorState]);

  const currentSchemaForChat = useMemo(() => {
    if (!activeV2) return null;
    return activeV2;
  }, [activeV2]);

  const {
    messages: chatMessages,
    isLoading: chatIsLoading,
    sendMessage: chatSendMessage,
    applyChanges: chatApply,
    revertChanges: chatRevert,
  } = useWorkflowChat({
    onApply: handleAIApply,
    currentSchema: currentSchemaForChat,
  });

  // ── Canvas workflow data ──
  const canvasWorkflow = useMemo(() => {
    if (!activeItem) return null;

    if (canvasMode === 'build') {
      return {
        id: activeItem.id,
        nodes: editorState.nodes,
        edges: editorState.edges,
        nodeStates: {},
      };
    }

    const nodeStates = {};
    if (activeItem.source === 'checklist') {
      const cl = activeItem.raw;
      cl.steps.forEach((step) => {
        nodeStates[step.id] = {
          status: step.completed ? 'satisfied' : 'available',
          satisfiedAt: step.completedAt,
          satisfiedBy: step.completedBy,
          completionSource: 'workflow',
        };
      });
      const allDone = cl.steps.every((s) => s.completed);
      nodeStates[`${cl.id}-end`] = { status: allDone ? 'satisfied' : 'blocked' };
    }

    return {
      id: activeItem.id,
      nodes: editorInitialState.nodes,
      edges: editorInitialState.edges,
      nodeStates,
    };
  }, [activeItem, canvasMode, editorState, editorInitialState]);

  const canvasNodeStates = canvasWorkflow?.nodeStates || {};

  // ── Handlers ──
  const discardBuildMode = useCallback(() => {
    if (canvasMode === 'build') {
      resetEditor(editorInitialState);
      setEditorDirty(false);
      setCanvasMode('execute');
      setSelectedNode(null);
    }
  }, [canvasMode, resetEditor, editorInitialState]);

  const handleSelect = useCallback((item) => {
    discardBuildMode();
    setActiveWorkflowId(activeWorkflowId === item.id ? null : item.id);
    setSelectedNode(null);
    setIsFullscreen(false);
  }, [activeWorkflowId, discardBuildMode]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  const handleModeToggle = useCallback(() => {
    setCanvasMode((prev) => (prev === 'build' ? 'execute' : 'build'));
    setSelectedNode(null);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  // ── Build mode: edit operations ──
  const handleAddNode = useCallback((type) => {
    const id = `${type}-${Date.now()}`;
    const newNode = {
      id,
      type,
      position: { x: 300, y: 200 },
      label: type === 'task' ? 'New Task' :
             type === 'gate' ? 'Choose path' :
             type === 'join' ? 'Wait for all' :
             type === 'parallel' ? 'Run in parallel' :
             type === 'timer' ? 'Timer' :
             type === 'subprocess' ? 'Sub-process' :
             'Finish',
      ...(type === 'task' && { completionType: 'checkbox', domain: activeItem?.domain || 'operations' }),
      ...(type === 'gate' && { options: ['Yes', 'No'] }),
      ...(type === 'timer' && { duration: 172800000 }), // Default 48h
    };
    setEditorState((s) => ({ ...s, nodes: [...s.nodes, newNode] }));
    setEditorDirty(true);
  }, [activeItem, setEditorState]);

  const handleConnect = useCallback((params) => {
    const newEdge = {
      id: `edge-${params.source}-${params.target}-${Date.now()}`,
      from: params.source,
      to: params.target,
      fromPort: params.sourceHandle || undefined,
    };
    setEditorState((s) => ({ ...s, edges: [...s.edges, newEdge] }));
    setEditorDirty(true);
  }, [setEditorState]);

  const handleDeleteNode = useCallback((nodeId) => {
    setEditorState((s) => ({
      nodes: s.nodes.filter((n) => n.id !== nodeId),
      edges: s.edges.filter((e) => e.from !== nodeId && e.to !== nodeId),
    }));
    setEditorDirty(true);
    setSelectedNode((sel) => (sel?.id === nodeId ? null : sel));
  }, [setEditorState]);

  const handleDeleteEdge = useCallback((edgeId) => {
    setEditorState((s) => ({ ...s, edges: s.edges.filter((e) => e.id !== edgeId) }));
    setEditorDirty(true);
  }, [setEditorState]);

  const handleUpdateNode = useCallback((nodeId, updates) => {
    setEditorState((s) => ({
      ...s,
      nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)),
    }));
    setEditorDirty(true);
    setSelectedNode((sel) => {
      if (sel?.id !== nodeId) return sel;
      return { ...sel, data: { ...sel.data, ...updates } };
    });
  }, [setEditorState]);

  const handleUpdateEdge = useCallback((edgeId, updates) => {
    setEditorState((s) => ({
      ...s,
      edges: s.edges.map((e) => (e.id === edgeId ? { ...e, ...updates } : e)),
    }));
    setEditorDirty(true);
  }, [setEditorState]);

  const handleAutoLayout = useCallback(() => {
    setEditorState((s) => ({
      ...s,
      nodes: s.nodes.map((n) => ({ ...n, position: { x: 0, y: 0 } })),
    }));
    setEditorDirty(true);
  }, [setEditorState]);

  const handleSave = useCallback(async () => {
    if (!activeTemplate) {
      toast({ title: 'Not supported', description: 'Saving edited checklists is not yet supported.', variant: 'destructive' });
      return;
    }
    try {
      await updateWorkflow(activeTemplate.id, {
        nodes: editorState.nodes,
        edges: editorState.edges,
      });
      setEditorDirty(false);
      setCanvasMode('execute');
      setSelectedNode(null);
      toast({ title: 'Workflow saved', description: 'Your changes have been saved.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.message || 'Unable to save', variant: 'destructive' });
    }
  }, [activeTemplate, editorState, updateWorkflow, toast]);

  const handleCancel = useCallback(() => {
    resetEditor(editorInitialState);
    setEditorDirty(false);
    setCanvasMode('execute');
    setSelectedNode(null);
    toast({ title: 'Changes discarded' });
  }, [resetEditor, editorInitialState, toast]);

  // ── Create / Delete workflow ──
  const handleCreateWorkflow = useCallback(async (workflowData) => {
    const created = await createWorkflow(workflowData);
    if (created) {
      setActiveWorkflowId(created.id);
      setCanvasMode('build');
      setViewMode('canvas');
      setSelectedNode(null);
      toast({ title: 'Workflow created', description: `"${created.name}" is ready to edit.` });
    }
  }, [createWorkflow, toast]);

  const handleCreateWithAI = useCallback(async ({ name, domain, cadence, regulatorySource, prompt }) => {
    try {
      const aiService = getWorkflowAIService();
      const schema = await aiService.generateWorkflow(prompt, { name, domain, cadence, regulatorySource });

      // Convert v2 machine to flat nodes/edges for storage
      const { nodes, edges } = machineToNodesEdges(schema.machine, schema.swimlanes);

      const workflowData = {
        name: schema.name,
        domain: schema.domain,
        cadence: schema.cadence,
        regulatorySource: schema.regulatorySource || null,
        nodes,
        edges,
        // Store v2 metadata alongside
        swimlanes: schema.swimlanes,
        machine: schema.machine,
        triggers: schema.triggers,
        guards: schema.guards,
        actions: schema.actions,
      };

      const created = await createWorkflow(workflowData);
      if (created) {
        setActiveWorkflowId(created.id);
        setCanvasMode('build');
        setViewMode('canvas');
        setSelectedNode(null);
        toast({
          title: 'Workflow generated',
          description: `"${created.name}" was created by AI. Review and edit as needed.`,
        });
      }
    } catch (err) {
      toast({ title: 'AI generation failed', description: err?.message || 'Unable to generate', variant: 'destructive' });
    }
  }, [createWorkflow, toast]);

  const handleDeleteWorkflow = useCallback(async (workflowId) => {
    await deleteWorkflow(workflowId);
    if (activeWorkflowId === workflowId) setActiveWorkflowId(null);
    toast({ title: 'Workflow deleted' });
  }, [deleteWorkflow, activeWorkflowId, toast]);

  // ── Layout state ──
  const isEditing = canvasMode === 'build';
  const showSidebar = activeItem != null; // Always show sidebar when workflow selected

  // ── Completion handlers (used by list accordion + canvas popover) ──
  const handleCompleteTask = useCallback((node, data) => {
    toast({ title: 'Task completed', description: `"${node.data?.label || node.label}" marked as done.` });
    // Future: dispatch to XState actor / API
  }, [toast]);

  const handleSkipTask = useCallback((node) => {
    toast({ title: 'Task skipped', description: `"${node.data?.label || node.label}" was skipped.` });
    // Future: dispatch SKIP event
  }, [toast]);

  const handleGateSelect = useCallback((nodeId, option) => {
    toast({ title: 'Path selected', description: `Chose "${option}".` });
    // Future: dispatch path event
  }, [toast]);

  // ── Sidebar props (AI chat + node config only, no completion form) ──
  const sidebarProps = {
    mode: canvasMode,
    selectedNode,
    taskLibrary,
    workflows,
    swimlanes: activeV2?.swimlanes || [],
    onDeselectNode: () => setSelectedNode(null),
    onUpdateNode: handleUpdateNode,
    onDeleteNode: handleDeleteNode,
    chatMessages,
    chatIsLoading,
    onChatSendMessage: chatSendMessage,
    onChatApply: chatApply,
    onChatRevert: chatRevert,
  };

  // ── Fullscreen ──
  if (isFullscreen && canvasWorkflow) {
    return (
      <>
        <div className="absolute inset-0 z-10 bg-white flex">
          <div className="flex-1 min-w-0 min-h-0">
            <WorkflowCanvas
              workflow={canvasWorkflow}
              nodeStates={canvasNodeStates}
              swimlanes={activeV2?.swimlanes || []}
              mode={canvasMode}
              onNodeClick={handleNodeClick}
              onModeToggle={handleModeToggle}
              onToggleFullscreen={handleToggleFullscreen}
              isFullscreen
              onAddNode={handleAddNode}
              onConnect={handleConnect}
              onDeleteNode={handleDeleteNode}
              onDeleteEdge={handleDeleteEdge}
              onUpdateEdge={handleUpdateEdge}
              onSave={handleSave}
              onCancel={handleCancel}
              onUndo={undo}
              onRedo={redo}
              canUndo={canUndo}
              canRedo={canRedo}
              isDirty={editorDirty}
              onAutoLayout={handleAutoLayout}
              onCompleteTask={handleCompleteTask}
              onSkipTask={handleSkipTask}
              onGateSelect={handleGateSelect}
            />
          </div>
          {showSidebar && (
            <div className="w-80 shrink-0 min-h-0">
              <WorkflowSidebar {...sidebarProps} />
            </div>
          )}
        </div>
        <CreateWorkflowDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreate={handleCreateWorkflow} onCreateWithAI={handleCreateWithAI} />
        <DeleteWorkflowDialog open={!!workflowToDelete} workflow={workflowToDelete} onClose={() => setWorkflowToDelete(null)} onConfirm={handleDeleteWorkflow} />
      </>
    );
  }

  // ── Normal layout ──
  return (
    <div className="flex flex-col h-full py-3">
      <SectionHeading
        size="sm"
        title="Workflows"
        className="mb-1.5 shrink-0"
        info="Operational workflows with step-by-step tracking. Each step can be completed here or from the Priority Queue."
        aiContext="pm.checklist-engine"
        action={
          <div className="flex items-center gap-2">
            <span className="text-body-sm text-text-muted">{allWorkflows.length} workflows</span>
            {!readOnly && (
              <Button variant="create" size="sm" onClick={() => setShowCreateDialog(true)}>
                <Plus size={ICON_PX.md} className="mr-1" /> New Workflow
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-1 min-h-0 gap-2">
        {/* LEFT: Workflow list */}
        {!isEditing && (
          <div className="flex flex-col w-72 shrink-0 min-h-0">
            <div className="shrink-0 mb-2">
              <FilterSearch value={search} onChange={setSearch} placeholder="Search workflows..." width="full" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 pr-0.5">
              {allWorkflows.length === 0 ? (
                <EmptyState title="No workflows" body="No workflows match your search." size="sm" />
              ) : (
                allWorkflows.map((item) => {
                  const progress = item.stepsTotal > 0 ? Math.round((item.stepsCompleted / item.stepsTotal) * 100) : 0;
                  const isComplete = item.stepsCompleted === item.stepsTotal && item.stepsTotal > 0;
                  const isSelected = activeWorkflowId === item.id;
                  const statusAccent = isComplete ? 'var(--green)' : progress >= 50 ? 'var(--yellow)' : 'var(--red-critical)';
                  const progressColor = isComplete ? 'bg-green-nn' : progress >= 50 ? 'bg-[var(--yellow)]' : 'bg-[var(--red-critical)]';

                  return (
                    <DomainCard
                      key={item.id}
                      domain={WF_DOMAIN_MAP[item.domain] || 'practice'}
                      accentColor={statusAccent}
                      accentPlacement="left"
                      density="compact"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelect(item)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(item); }}
                      className={[
                        'group flex items-start gap-2 p-2 cursor-pointer transition-all hover:shadow-sm',
                        isSelected ? 'ring-1 ring-aqua shadow-sm' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-body-sm font-medium text-text-dark truncate">{item.name}</span>
                          {item.source === 'template' && !readOnly && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setWorkflowToDelete(item.raw);
                              }}
                              className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-critical p-0.5 rounded"
                              title="Delete workflow"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-caption text-text-muted">
                          <span className="capitalize">{item.domain}</span>
                          <span>{item.cadence}</span>
                          {item.regulatorySource && REGULATORY_BADGE[item.regulatorySource] && (
                            <StatusBadge variant={REGULATORY_BADGE[item.regulatorySource].variant}>
                              {REGULATORY_BADGE[item.regulatorySource].label}
                            </StatusBadge>
                          )}
                        </div>
                        {item.stepsTotal > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-caption text-text-muted shrink-0">{item.stepsCompleted}/{item.stepsTotal}</span>
                            <div className="flex-1 h-1 bg-nn-border rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${progressColor}`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </DomainCard>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* CENTER: Canvas/List view */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {activeItem && canvasMode === 'execute' && (
            <div className="flex items-center justify-between mb-2 shrink-0">
              <FilterToggle
                value={viewMode}
                onChange={setViewMode}
                options={[{ value: 'list', label: 'List' }, { value: 'canvas', label: 'Canvas' }]}
              />
              <span className="text-caption text-text-muted">
                {activeItem.stepsCompleted}/{activeItem.stepsTotal} steps
              </span>
            </div>
          )}

          <div className="flex-1 min-h-0">
            {canvasWorkflow ? (
              viewMode === 'list' && canvasMode === 'execute' ? (
                <WorkflowExecutionList
                  workflow={canvasWorkflow}
                  nodeStates={canvasNodeStates}
                  onCompleteTask={handleCompleteTask}
                  onSkipTask={handleSkipTask}
                  onGateSelect={handleGateSelect}
                />
              ) : (
                <WorkflowCanvas
                  workflow={canvasWorkflow}
                  nodeStates={canvasNodeStates}
                  swimlanes={activeV2?.swimlanes || []}
                  mode={canvasMode}
                  onNodeClick={handleNodeClick}
                  onModeToggle={handleModeToggle}
                  onToggleFullscreen={handleToggleFullscreen}
                  onAddNode={handleAddNode}
                  onConnect={handleConnect}
                  onDeleteNode={handleDeleteNode}
                  onDeleteEdge={handleDeleteEdge}
                  onUpdateEdge={handleUpdateEdge}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onUndo={undo}
                  onRedo={redo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  isDirty={editorDirty}
                  onAutoLayout={handleAutoLayout}
                  onCompleteTask={handleCompleteTask}
                  onSkipTask={handleSkipTask}
                  onGateSelect={handleGateSelect}
                />
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full rounded-xl border border-slate-200 bg-white text-slate-400">
                <span className="text-lg mb-1">Select a workflow</span>
                <span className="text-body-sm">Choose from the list to view steps and progress</span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Sidebar (always visible when workflow selected) */}
        {showSidebar && (
          <div className="w-80 shrink-0 min-h-0">
            <WorkflowSidebar {...sidebarProps} />
          </div>
        )}
      </div>

      <CreateWorkflowDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreate={handleCreateWorkflow} onCreateWithAI={handleCreateWithAI} />
      <DeleteWorkflowDialog open={!!workflowToDelete} workflow={workflowToDelete} onClose={() => setWorkflowToDelete(null)} onConfirm={handleDeleteWorkflow} />
    </div>
  );
}

export default PMChecklistsTab;
